import { dateUtils } from "../utils/DateUtils";
import { AbstractEntityService } from "./AbstractEntityService";
import { restService } from "./RestService";

export class OrdersService extends AbstractEntityService {
    constructor(baseUrl) {
        super();
        this.baseUrl = baseUrl;
    }

    ordersCollectionUrl(propertyId) {
        return `${this.baseUrl}/properties/${propertyId}/orders`;
    }

    /**
     * Fetches all orders for a property
     * @param {string} propertyId - Property ID
     * @returns {Promise<any>} - List of orders
     */
    async findAll(propertyId) {
        const url = this.ordersCollectionUrl(propertyId);

        try {
            const orders = await super.find(url);
            orders.forEach(order => dateUtils.parseTimestampsToDates(order));

            return orders;
        } catch (error) {
            this.handleError(error, "Unable to load orders - please see server logs for more details");
        }
    };

    /**
     * Fetches a specific order by ID
     * @param {string} propertyId - Property ID
     * @param {string|number} id - Order ID
     * @returns {Promise<any>} - Order data
     */
    async findById(propertyId, id) {
        const url = `${this.ordersCollectionUrl(propertyId)}/${id}`;

        try {
            const order = await super.find(url);
            dateUtils.parseTimestampsToDates(order);

            return order;
        } catch (error) {
            this.handleError(error, `Unable to load order ${id} - please see server logs for more details`);
        }
    };

    /**
     * Creates a new order
     * @param {string} propertyId - Property ID
     * @param {Object} order - OrderRequest payload
     * @returns {Promise<Object>} - Created order
     */
    async create(propertyId, order) {
        const url = this.ordersCollectionUrl(propertyId);

        try {
            let payload = { ...order };
            dateUtils.parseDatesToTimestamps(payload);

            const createdOrder = await super.create(url, payload);
            dateUtils.parseTimestampsToDates(createdOrder);
            return createdOrder;
        } catch (error) {
            this.handleError(error, "Unable to create order - please see server logs for more details");
        }
    };

    /**
     * Updates an existing order
     * @param {string} propertyId - Property ID
     * @param {Object} order - Updated order data
     * @returns {Promise<any>} - Updated order
     */
    async update(propertyId, order) {
        const url = `${this.ordersCollectionUrl(propertyId)}/${order.id}`;

        try {
            let payload = { ...order };
            dateUtils.parseDatesToTimestamps(payload);

            const updatedOrder = await super.update(url, payload);

            dateUtils.parseTimestampsToDates(updatedOrder);

            return updatedOrder;
        } catch (error) {
            this.handleError(error, `Unable to update order ${order.id} - please see server logs for more details`);
        }
    };

    /**
     * Deletes an order by ID
     * @param {string} propertyId - Property ID
     * @param {string|number} id - Order ID
     * @returns {Promise<any>} - Deletion confirmation
     */
    async delete(propertyId, id) {
        const url = `${this.ordersCollectionUrl(propertyId)}/${id}`;

        try {
            return await super.delete(url);
        } catch (error) {
            this.handleError(error, `Unable to delete order ${id} - please see server logs for more details`);
        }
    };

    /**
     * Moves a DRAFT order to PENDING_PAYMENT and optionally initiates PayU payment.
     * For PayU, the checkout redirect URL is returned in the X-Payment-Redirect-Url response header.
     * @param {string} propertyId
     * @param {string} orderId
     * @param {'payu'|'bank_transfer'|null} [paymentMethod]
     * @param {string[]} [paymentIds] - optional; unpaid payments due on or before today when omitted
     * @returns {Promise<{ order: Object, paymentRedirectUrl: string|null }>}
     */
    async checkout(propertyId, orderId, paymentMethod, paymentIds) {
        const url = `${this.ordersCollectionUrl(propertyId)}/${orderId}/checkout`;
        const body = {};
        if (paymentMethod) body.paymentMethod = paymentMethod;
        if (paymentIds && paymentIds.length > 0) body.paymentIds = paymentIds;
        try {
            const { data: order, headers } = await restService.postRaw(url, body);
            dateUtils.parseTimestampsToDates(order);
            const paymentRedirectUrl = headers['x-payment-redirect-url'] ?? null;
            return { order, paymentRedirectUrl };
        } catch (error) {
            this.handleError(error, `Unable to confirm payment for order ${orderId}`);
        }
    }

    /**
     * Reverts a failed/abandoned PayU checkout.
     * @param {string} propertyId
     * @param {string} id
     * @param {string[]} [paymentIds] - PayU payment IDs from cancelUrl; clears all unpaid methods when omitted
     * @returns {Promise<Object>} - Order (DRAFT when no unpaid payment method remains)
     */
    async abandonPayment(propertyId, id, paymentIds) {
        const url = `${this.ordersCollectionUrl(propertyId)}/${id}/abandon-payment`;
        const body = {};
        if (paymentIds && paymentIds.length > 0) body.paymentIds = paymentIds;
        try {
            const order = await super.create(url, body);
            dateUtils.parseTimestampsToDates(order);
            return order;
        } catch (error) {
            this.handleError(error, `Unable to revert payment for order ${id}`);
        }
    }

    async updatePayments(propertyId, id, payments) {
        const url = `${this.ordersCollectionUrl(propertyId)}/${id}/payments`;

        try {
            return await super.update(url, payments);
        } catch (error) {
            this.handleError(error, `Unable to update payments - please see server logs for more details`);
        }

    }
}